import ChangePasswordModal from '@components/modals/ChangePasswordModal';
import LogRocketConsentModal from '@components/modals/LogRocketConsentModal';
import CustomModal from '@components/CustomModal';
import WolfButton from '@components/ui/WolfButton';
import { DropdownSelectedItem } from '@components/ui/WolfDropdown';
import ProfileSection from '@components/settings/ProfileSection';
import OrganizationsSection from '@components/settings/OrganizationsSection';
import GeneralSettingsSection from '@components/settings/GeneralSettingsSection';
import DeveloperSettingsSection from '@components/settings/DeveloperSettingsSection';
import NotificationsSection from '@components/settings/NotificationsSection';
import { graphQlClient, updateHeaders } from '@config/graphqlClient';
import { getSessionUuid, getTokenLocalStorage, getTokenSessionStorage } from '@config/storage';
import IntegrationsGraphQL from '@graphql/integration.queries';
import UserGraphQL from '@graphql/user.queries';
import UserOrganizationGraphQL from '@graphql/userOrganization.queries';
import { useLoader } from '@hooks/useLoader';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import {
  getIntegrationRequest,
  IntegrationProvider,
  IntegrationStatus,
  IntegrationType,
} from '@models/integration.model';
import {
  getSelfWithOrganizationRequest,
  leaveOrganizationRequest,
  updateDefaultOrganizationRequest,
  updateOwnUserRequest,
  updateUserPreferencesRequest,
  User,
  UserRoles,
  UserPreferences,
  NotificationPreferences,
  NotificationChannels,
  ConsentOption,
} from '@models/user.model';
import { Box, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { clearOrganizationData, setOrganizationData, switchOrganization } from '@redux/reducers/organization.reducer';
import { setUserDetails, setUserOrganizations, setUserPreferences, setShowTutorial } from '@redux/reducers/user.reducer';
import {
  permanentlyDisableWolfie,
  permanentlyReenableWolfie,
  setWolfieAutoEnableMic,
  setWolfieDevPanelEnabled,
  setWolfieSpeaking,
  setWolfieVideoMode,
  setWolfieWakeWordEnabled,
} from '@redux/reducers/wolfie.reducer';
import { RootState } from '@redux/store';
import React, { useEffect, useState } from 'react';

const SettingsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [userData, setUserData] = useState<User>({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showLogRocketModal, setShowLogRocketModal] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState<string | null>(null); // organizationId when showing confirm dialog
  const [isSlackConnected, setIsSlackConnected] = useState(false);

  const localeCommon = useTranslation('common');
  const localeSettings = useTranslation('settings');
  const localeLogRocket = useTranslation('logRocket');

  const { setLoading } = useLoader();
  const {
    role: currentRole,
    rememberMe,
    userId,
    email,
    firstName: currentFirstName,
    lastName: currentLastName,
    defaultOrganizationId,
    organizations: reduxOrganizations,
    preferences,
  } = useAppSelector((state: RootState) => state.user);
  const { organizationId } = useAppSelector((state: RootState) => state.organization);
  const { isImpersonating } = useAppSelector((state: RootState) => state.impersonate);
  const {
    permanentlyDisabled,
    speaking: wolfieSpeaking,
    videoMode: wolfieVideoMode,
    messages: wolfieMessages,
    autoEnableMic,
    wakeWordEnabled,
    devPanelEnabled,
    enabled: wolfieEnabled,
  } = useAppSelector((state: RootState) => state.wolfie);

  useEffect(() => {
    getSelf();
    fetchIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchIntegrations = async () => {
    try {
      const data: getIntegrationRequest = await graphQlClient.request(IntegrationsGraphQL.queries.getIntegrations);
      const slackIntegration = data.getIntegrations.find(
        (integration) =>
          integration.integrationType === IntegrationType.MESSAGING &&
          integration.integrationProvider === IntegrationProvider.SLACK &&
          integration.status === IntegrationStatus.ENABLED,
      );
      setIsSlackConnected(!!slackIntegration);
    } catch (e) {
      // Silently fail - integrations list is optional for display
    }
  };

  const getSelf = async () => {
    try {
      setLoading(true);
      const data: getSelfWithOrganizationRequest = await graphQlClient.request(
        UserGraphQL.queries.getSelfWithOrganization,
      );
      const user = data.getSelfWithOrganization.user;
      const role = data.getSelfWithOrganization.role;
      const organizations = data.getSelfWithOrganization.organizations;

      setUserData({ ...user, role });

      // Update Redux with user details and organizations
      const hasUserDataChanged =
        user.userId !== userId ||
        user.email !== email ||
        user.firstName !== currentFirstName ||
        user.lastName !== currentLastName ||
        user.defaultOrganizationId !== defaultOrganizationId ||
        role !== currentRole;

      const hasOrganizationsChanged =
        !reduxOrganizations ||
        !organizations ||
        reduxOrganizations.length !== organizations.length ||
        reduxOrganizations.some(
          (reduxOrg, index) =>
            reduxOrg.organizationId !== organizations[index]?.organizationId ||
            reduxOrg.name !== organizations[index]?.name ||
            reduxOrg.role !== organizations[index]?.role,
        );

      if (hasUserDataChanged) {
        dispatch(setUserDetails({ ...user, role }));
      }

      // Always update preferences from API response
      if (user.preferences) {
        dispatch(setUserPreferences(user.preferences));
      }

      // Only update organizations in Redux if they have actually changed
      if (organizations && hasOrganizationsChanged) {
        dispatch(setUserOrganizations(organizations));
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchOrganization = async (orgId: string, orgName: string, orgDescription?: string) => {
    if (orgId === organizationId) {
      dispatch(
        appendActionMessage({
          message: 'You are already in this organization.',
          type: SnackType.INFO,
        }),
      );
      return;
    }

    try {
      setLoading(true);

      // Use the same approach as AvatarDropdown - simple and proven
      dispatch(switchOrganization(orgId, orgName, orgDescription));

      updateHeaders({
        'X-Organization-Id': orgId,
      });

      const userData: getSelfWithOrganizationRequest = await graphQlClient.request(
        UserGraphQL.queries.getSelfWithOrganization,
      );

      const user = userData.getSelfWithOrganization.user;
      const role = userData.getSelfWithOrganization.role;
      const organization = userData.getSelfWithOrganization.organization;
      const organizations = userData.getSelfWithOrganization.organizations || [];

      if (organization) {
        dispatch(setOrganizationData(organization));
      }
      dispatch(setUserDetails({ ...user, role }));
      dispatch(setUserOrganizations(organizations));

      dispatch(
        appendActionMessage({
          message: `Switched to organization: ${organization?.name || 'Unknown'}`,
          type: SnackType.SUCCESS,
        }),
      );
    } catch (error: any) {
      console.error('Error switching organization:', error);
      dispatch(
        appendActionMessage({
          message: 'Failed to switch organization. Please try again.',
          type: SnackType.ERROR,
        }),
      );

      // Revert if failed
      if (organizationId) {
        dispatch(switchOrganization(organizationId, '', ''));
        updateHeaders({
          'X-Organization-Id': organizationId,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateName = async (firstName: string, lastName: string) => {
    if (!firstName.trim() || !lastName.trim()) {
      dispatch(
        appendActionMessage({
          message: localeCommon['fullNameRequired'] || 'Full name is required',
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    try {
      setLoading(true);
      const updateResult: updateOwnUserRequest = await graphQlClient.request(UserGraphQL.mutations.updateOwnUser, {
        user: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        },
      });

      if (updateResult.updateOwnUser) {
        const updatedUser = { ...userData, firstName: firstName.trim(), lastName: lastName.trim() };
        setUserData(updatedUser);
        dispatch(setUserDetails(updatedUser));
        dispatch(
          appendActionMessage({
            message: localeCommon['profileUpdatedSuccessfully'] || 'Profile updated successfully',
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleDisabled = () => {
    if (permanentlyDisabled) {
      dispatch(permanentlyReenableWolfie());
    } else {
      dispatch(permanentlyDisableWolfie());
    }
  };

  const handleTutorialLaunch = () => {
    dispatch(setShowTutorial(true));
  };

  const toggleEnableVoiceMode = () => {
    if (wolfieVideoMode) {
      dispatch(setWolfieVideoMode(false));
    }
    if (wolfieSpeaking) {
      speechSynthesis.cancel();
    } else {
      const lastAssistantMessage = [...wolfieMessages].reverse().find((message) => message.role === 'assistant');

      const speech = new SpeechSynthesisUtterance(lastAssistantMessage?.content);
      speech.lang = 'en-US';
      speechSynthesis.speak(speech);
    }
    dispatch(setWolfieSpeaking(!wolfieSpeaking));
  };

  const toggleEnableAvatarMode = () => {
    if (wolfieSpeaking) {
      speechSynthesis.cancel();
      dispatch(setWolfieSpeaking(false));
    }
    dispatch(setWolfieVideoMode(!wolfieVideoMode));
  };

  const toggleAutoEnableMic = () => {
    dispatch(setWolfieAutoEnableMic(!autoEnableMic));
  };

  const toggleWakeWordEnabled = () => {
    dispatch(setWolfieWakeWordEnabled(!wakeWordEnabled));
  };

  const toggleEnableDevPanel = () => {
    dispatch(setWolfieDevPanelEnabled(!devPanelEnabled));
  };

  const handleDefaultOrganizationChange = async (selectedItems: DropdownSelectedItem[]) => {
    if (selectedItems.length === 0) return;

    const newDefaultOrgId = selectedItems[0].value;
    if (newDefaultOrgId === userData.defaultOrganizationId) return;

    try {
      setLoading(true);
      const updateResult: updateDefaultOrganizationRequest = await graphQlClient.request(
        UserGraphQL.mutations.updateDefaultOrganization,
        {
          input: {
            defaultOrganizationId: newDefaultOrgId,
          },
        },
      );

      if (updateResult.updateDefaultOrganization) {
        const updatedUser = { ...userData, defaultOrganizationId: newDefaultOrgId };
        setUserData(updatedUser);
        dispatch(setUserDetails(updatedUser));
        dispatch(
          appendActionMessage({
            message: localeSettings['defaultOrganizationUpdated'] || 'Default organization updated successfully',
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogRocketConsentChange = async (option: ConsentOption) => {
    const previousConsent = preferences?.allowLogRocketRecording;

    try {
      setLoading(true);
      const updatedPreferences: UserPreferences = {
        ...preferences,
        allowLogRocketRecording: option,
      };

      const updateResult: updateUserPreferencesRequest = await graphQlClient.request(
        UserGraphQL.mutations.updateUserPreferences,
        {
          input: {
            preferences: updatedPreferences,
          },
        },
      );

      if (updateResult.updateUserPreferences) {
        dispatch(setUserPreferences(updatedPreferences));

        dispatch(
          appendActionMessage({
            message: localeSettings['sessionRecordingUpdated'] || 'Session recording preference updated successfully',
            type: SnackType.SUCCESS,
          }),
        );
        setShowLogRocketModal(false);

        // Reload page to ensure LogRocket state is properly reset
        // This prevents scenarios where LogRocket remains initialized/identified after switching consent
        if (previousConsent && previousConsent !== option) {
          // Small delay to allow success message to be seen
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  // Badge pairs that share the same sidebar badge
  const BADGE_PAIRS: Record<string, keyof NotificationPreferences> = {
    discrepancies: 'wolfPendings',
    wolfPendings: 'discrepancies',
    assignments: 'executionReminders',
    executionReminders: 'assignments',
  };

  const handleNotificationPreferenceChange = async (
    category: keyof NotificationPreferences,
    channel: keyof NotificationChannels,
    value: boolean
  ) => {
    try {
      setLoading(true);
      const currentCategoryPrefs = preferences?.notificationPreferences?.[category] || {};
      let updatedNotificationPreferences = {
        ...preferences?.notificationPreferences,
        [category]: {
          ...currentCategoryPrefs,
          [channel]: value,
        },
      };

      // Sync badge value with paired category
      if (channel === 'badge' && BADGE_PAIRS[category]) {
        const pairedCategory = BADGE_PAIRS[category];
        const pairedCategoryPrefs = preferences?.notificationPreferences?.[pairedCategory] || {};
        updatedNotificationPreferences = {
          ...updatedNotificationPreferences,
          [pairedCategory]: {
            ...pairedCategoryPrefs,
            badge: value,
          },
        };
      }

      const updatedPreferences: UserPreferences = {
        ...preferences,
        notificationPreferences: updatedNotificationPreferences,
      };

      const updateResult: updateUserPreferencesRequest = await graphQlClient.request(
        UserGraphQL.mutations.updateUserPreferences,
        {
          input: {
            preferences: updatedPreferences,
          },
        },
      );

      if (updateResult.updateUserPreferences) {
        dispatch(setUserPreferences(updatedPreferences));

        dispatch(
          appendActionMessage({
            message: localeSettings['notificationPreferencesUpdated'],
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleManualOrgUpdate = async (all: boolean = false) => {
    try {
      setLoading(true);
      const sessionUuid = getSessionUuid();
      const endpoint = all ? 'ai/ingest-manual-all' : 'ai/ingest-manual';
      const body = all ? {} : { organizationId: organizationId };

      const response = await fetch(process.env.REACT_APP_BACKEND_URL + endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${rememberMe ? getTokenLocalStorage() : getTokenSessionStorage()}`,
          'Session-Uuid': sessionUuid,
          'X-Organization-Id': organizationId || '',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      dispatch(
        appendActionMessage({
          message: all
            ? localeSettings['allOrganizationsUpdatedSuccessfully'] || 'All organizations data updated successfully'
            : localeSettings['organizationUpdatedSuccessfully'] || 'Organization data updated successfully',
          type: SnackType.SUCCESS,
        }),
      );
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveOrganization = async (orgId: string) => {
    try {
      setLoading(true);
      const result: leaveOrganizationRequest = await graphQlClient.request(
        UserOrganizationGraphQL.mutations.leaveOrganization,
        {
          organizationId: orgId,
        },
      );

      if (result.leaveOrganization) {
        dispatch(
          appendActionMessage({
            message: 'Successfully left the organization',
            type: SnackType.SUCCESS,
          }),
        );

        // Refresh user data to get updated organizations list
        await getSelf();

        // If user left their current organization and has remaining organizations, switch to the first one
        if (orgId === organizationId) {
          // After getSelf(), check the updated organizations list
          const updatedUserData: getSelfWithOrganizationRequest = await graphQlClient.request(
            UserGraphQL.queries.getSelfWithOrganization,
          );
          const remainingOrgs = updatedUserData.getSelfWithOrganization.organizations;

          if (remainingOrgs && remainingOrgs.length > 0) {
            await handleSwitchOrganization(
              remainingOrgs[0].organizationId,
              remainingOrgs[0].name,
              remainingOrgs[0].description,
            );
          } else {
            // Clear organization data when user has no organizations left
            dispatch(clearOrganizationData());
          }
          // If no remaining organizations, App.tsx routing will show NoOrganizationScreen
        }
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
      setShowLeaveConfirm(null);
    }
  };

  const canLeaveOrganization = (orgId: string) => {
    const org = reduxOrganizations?.find((o) => o.organizationId === orgId);
    if (!org) return false;

    // Can't leave if you're the SUPER_ADMIN of the organization
    if (org.role === UserRoles.SUPER_ADMIN) {
      return false;
    }

    // Can't leave if you're the owner of the organization
    if (org.role === UserRoles.OWNER) {
      return false;
    }

    return true;
  };

  const getLeaveTooltipText = (orgId: string) => {
    const org = reduxOrganizations?.find((o) => o.organizationId === orgId);
    if (!org) return 'Organization not found';

    if (org.role === UserRoles.SUPER_ADMIN) {
      return 'Super Admins cannot leave their organization';
    }

    if (org.role === UserRoles.OWNER) {
      return 'Owners cannot leave their organization';
    }

    return 'Leave this organization';
  };

  return (
    <Box className="flex flex-wrap w-full max-w-screen-lg p-4 justify-center mx-auto">
      <Box className={`w-full ${wolfieEnabled ? 'mb-32' : ''}`}>
        <Typography variant="h2" className="!my-8 text-center sm:text-left">
          {localeSettings['profile']}
        </Typography>

        {/* Profile Section */}
        <ProfileSection
          userData={userData}
          reduxOrganizations={reduxOrganizations || []}
          onUpdateName={handleUpdateName}
          onDefaultOrganizationChange={handleDefaultOrganizationChange}
        />

        {/* Organizations Section */}
        <OrganizationsSection
          organizations={reduxOrganizations || []}
          currentOrgId={organizationId || ''}
          currentRole={currentRole}
          onSwitch={handleSwitchOrganization}
          onLeave={setShowLeaveConfirm}
          canLeaveOrganization={canLeaveOrganization}
          getLeaveTooltipText={getLeaveTooltipText}
        />

        <Typography variant="h2" className="!my-8 !mt-12 text-center sm:text-left">
          {localeSettings['configuration']}
        </Typography>

        {/* General Settings */}
        <GeneralSettingsSection
          loginMethod={userData.loginMethod || ''}
          preferences={preferences}
          permanentlyDisabled={permanentlyDisabled}
          isImpersonating={isImpersonating}
          onPasswordChange={() => setShowPasswordModal(true)}
          onLogRocketChange={() => setShowLogRocketModal(true)}
          onWolfieToggle={toggleDisabled}
          onTutorialLaunch={handleTutorialLaunch}
        />

        {/* Notification Preferences */}
        <NotificationsSection
          notificationPreferences={preferences?.notificationPreferences}
          onPreferenceChange={handleNotificationPreferenceChange}
          isSlackConnected={isSlackConnected}
        />

        {/* Developer Settings - Only for SUPER_ADMIN */}
        {currentRole === UserRoles.SUPER_ADMIN && (
          <DeveloperSettingsSection
            autoEnableMic={autoEnableMic}
            wakeWordEnabled={wakeWordEnabled}
            wolfieSpeaking={wolfieSpeaking}
            wolfieVideoMode={wolfieVideoMode}
            devPanelEnabled={devPanelEnabled}
            onAutoEnableMicToggle={toggleAutoEnableMic}
            onWakeWordToggle={toggleWakeWordEnabled}
            onVoiceModeToggle={toggleEnableVoiceMode}
            onAvatarModeToggle={toggleEnableAvatarMode}
            onDevPanelToggle={toggleEnableDevPanel}
            onManualOrgUpdate={handleManualOrgUpdate}
          />
        )}
      </Box>

      <ChangePasswordModal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} />

      {/* LogRocket Consent Modal */}
      <CustomModal
        isOpen={showLogRocketModal}
        onClose={() => setShowLogRocketModal(false)}
        hideClose={false}
        showButtons={false}
        title={localeLogRocket['title']}
        maxWidth="600px">
        <LogRocketConsentModal
          onConsent={handleLogRocketConsentChange}
          initialValue={preferences?.allowLogRocketRecording || 'identified'}
        />
      </CustomModal>

      {/* Leave Organization Confirmation Dialog */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Leave Organization?</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to leave{' '}
              <strong>{reduxOrganizations?.find((org) => org.organizationId === showLeaveConfirm)?.name}</strong>? You
              will lose access to all processes, documents, and data associated with this organization.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              This action cannot be undone. You will need to be re-invited to rejoin this organization.
            </p>
            <div className="flex gap-3 justify-end">
              <WolfButton color="secondary" onClick={() => setShowLeaveConfirm(null)}>
                Cancel
              </WolfButton>
              <WolfButton
                color="primary"
                onClick={() => handleLeaveOrganization(showLeaveConfirm)}
                className="!bg-red-600 !border-red-600 hover:!bg-red-700">
                Leave Organization
              </WolfButton>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default SettingsPage;
